import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom'
import { Layout } from '@/components/Layout'
import { LocaleProvider, LocaleTree } from '@/i18n/LocaleProvider'
import { LocaleSync } from '@/i18n/LocaleSync'
import { AuthProvider, RequireAuth } from '@/lib/auth'
import { LiveUpdates } from '@/lib/events'
import { ActivityTracker } from '@/components/ActivityTracker'
import { Activity } from '@/pages/Activity'
import { Accounts } from '@/pages/Accounts'
import { Counterparties } from '@/pages/Counterparties'
import { Dashboard } from '@/pages/Dashboard'
import { ImportPage } from '@/pages/Import'
import { Login } from '@/pages/Login'
import { OrderForm } from '@/pages/OrderForm'
import { Orders } from '@/pages/Orders'
import { Review } from '@/pages/Review'
import { Screenshots } from '@/pages/Screenshots'
import { Inventory } from '@/pages/Inventory'
import { Profit } from '@/pages/Profit'
import { Settings } from '@/pages/Settings'
import { Reconciliation } from '@/pages/Reconciliation'
import { Reports } from '@/pages/Reports'
import { Alerts } from '@/pages/Alerts'
import { CounterpartyProfile } from '@/pages/CounterpartyProfile'
import { Transactions } from '@/pages/Transactions'
import { Market } from '@/pages/Market'
import { Privacy } from '@/pages/Privacy'

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 15_000,
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
})

export default function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <LocaleProvider>
        <AuthProvider>
          <LocaleSync />
          <LiveUpdates />
          <BrowserRouter>
            <ActivityTracker />
            <LocaleTree>
            <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/privacy" element={<Privacy />} />
            <Route
              element={
                <RequireAuth>
                  <Layout />
                </RequireAuth>
              }
            >
              <Route path="/" element={<Dashboard />} />
              <Route path="/orders" element={<Orders />} />
              <Route path="/orders/new" element={<OrderForm />} />
              <Route path="/orders/:orderId" element={<OrderForm />} />
              <Route path="/import" element={<ImportPage />} />
              <Route path="/screenshots" element={<Screenshots />} />
              <Route path="/screenshots/:jobId" element={<Review />} />
              <Route path="/accounts" element={<Accounts />} />
              <Route path="/counterparties" element={<Counterparties />} />
              <Route path="/counterparties/:counterpartyId" element={<CounterpartyProfile />} />
              <Route path="/alerts" element={<Alerts />} />
              <Route path="/activity" element={<Activity />} />
              <Route path="/inventory" element={<Inventory />} />
              <Route path="/pnl" element={<Profit />} />
              <Route path="/transactions" element={<Transactions />} />
              <Route path="/reconciliation" element={<Reconciliation />} />
              <Route path="/reports" element={<Reports />} />
              <Route path="/settings" element={<Settings />} />
              <Route path="/market" element={<Market />} />
              <Route path="*" element={<Navigate to="/" replace />} />
            </Route>
            </Routes>
            </LocaleTree>
          </BrowserRouter>
        </AuthProvider>
      </LocaleProvider>
    </QueryClientProvider>
  )
}
